// JournalContent.js - Visar och redigerar journalinnehåll

import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { UserContext } from '../../UserContext';
import TextAreaWithPhrases from '../TextAreaWithPhrases';
import RecordingPanel from '../RecordingPanel/RecordingPanel';
import PaymentModal from '../Calendar/PaymentModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { getAppointment } from '../Calendar/calendarApi';
import { getAmendments, createAmendment, getJournalEntry } from './journalApi';
import { serializeSectionsForPmo, transferToPmo } from './pmoClient';
import useDraftAutosave from './useDraftAutosave';
import { useRecordingSession } from '../../recording/RecordingContext';
import '../JournalEditor/JournalEditor.css'; // autosave-indicator--*
import './JournalContent.css';

// Signerat tillägg (Spår 4) — visar befintliga tillägg och låter ursprunglig
// författare lägga till nya (append-only, auto-signeras av backend). Speglar
// AmendmentSection-mönstret från diktat-editorn men lever i det manuella flödet.
const JournalAmendments = ({ entryId, readOnly = false }) => {
  const [amendments, setAmendments] = useState([]);
  const [canAdd, setCanAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAmendments(entryId)
      .then((data) => {
        if (cancelled) return;
        setAmendments(data.amendments || []);
        setCanAdd(Boolean(data.can_add_amendment));
      })
      .catch((err) => {
        console.error('Kunde inte ladda tillägg:', err);
        if (!cancelled) toast.error('Kunde inte ladda tillägg');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [entryId]);

  const formatDateTime = (dateStr) =>
    dateStr ? new Date(dateStr).toLocaleString('sv-SE') : '-';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      toast.error('Tilläggstext krävs');
      return;
    }
    try {
      setSubmitting(true);
      const newAmendment = await createAmendment(entryId, {
        amendment_text: text.trim(),
        amendment_reason: reason.trim() || null
      });
      setAmendments((prev) => [...prev, newAmendment]);
      setText('');
      setReason('');
      setShowForm(false);
      toast.success('Tillägg sparat och signerat');
    } catch (err) {
      console.error('Kunde inte spara tillägg:', err);
      toast.error(err.response?.data?.error || 'Kunde inte spara tillägg');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="journal-amendments">
        <div className="jc-amendment-loading">Laddar tillägg...</div>
      </div>
    );
  }

  return (
    <div className="journal-amendments">
      <div className="jc-amendment-header">
        <h4>
          <i className="fas fa-history"></i> Tillägg ({amendments.length})
        </h4>
        {canAdd && !readOnly && !showForm && (
          <button
            type="button"
            className="jc-amendment-add-btn"
            onClick={() => setShowForm(true)}
          >
            <i className="fas fa-plus"></i> Lägg till tillägg
          </button>
        )}
      </div>

      {amendments.length > 0 && (
        <div className="jc-amendment-list">
          {amendments.map((a) => (
            <div key={a.amendment_id} className="jc-amendment-item">
              <div className="jc-amendment-meta">
                <span><i className="fas fa-clock"></i> {formatDateTime(a.created_at)}</span>
                <span><i className="fas fa-user"></i> {a.created_by_name}</span>
                <span className="jc-amendment-signed"><i className="fas fa-check-circle"></i> Signerat</span>
              </div>
              {a.amendment_reason && (
                <div className="jc-amendment-reason">
                  <strong>Anledning:</strong> {a.amendment_reason}
                </div>
              )}
              <div className="jc-amendment-text">{a.amendment_text}</div>
            </div>
          ))}
        </div>
      )}

      {amendments.length === 0 && !showForm && (
        <div className="jc-amendment-empty">Inga tillägg har gjorts.</div>
      )}

      {showForm && (
        <form className="jc-amendment-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Anledning till tillägg (valfritt)</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="T.ex. Komplettering efter uppföljning"
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label>Tillägg *</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Skriv ditt tillägg här..."
              rows={4}
              disabled={submitting}
              required
            />
          </div>
          <div className="jc-amendment-form-info">
            <i className="fas fa-info-circle"></i> Tillägget signeras automatiskt när du sparar. Originalet ändras aldrig.
          </div>
          <div className="jc-amendment-form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setShowForm(false); setText(''); setReason(''); }}
              disabled={submitting}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="btn btn-success"
              disabled={submitting || !text.trim()}
            >
              {submitting ? 'Sparar...' : 'Spara och signera'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const JournalContent = ({
  journal,
  isEditing,
  onEdit,
  onSign,
  onDelete,
  onCancel,
  saving,
  autoRecord = false,
  onAutoRecordConsumed,
  unpaidAppointments = [],
  onAppointmentPaid,
  onAutosaved,
  // Skrivskyddad referensvy (Spår J2): inga åtgärder, inget tilläggsformulär
  readOnly = false
}) => {
  // Hämta user för att kolla om inbyggt journalsystem används
  const { user } = useContext(UserContext);
  const useInternalJournal = user?.clinic?.use_internal_journal || false;

  // Betalning (öppnar samma modal som kalendern)
  const [payAppointment, setPayAppointment] = useState(null);
  const [loadingPay, setLoadingPay] = useState(false);
  const [paidLocally, setPaidLocally] = useState(false);

  // Vilken bokning ska "Betala besök" gälla?
  // 1. Journalens länkade bokning om den är obetald.
  // 2. Annars patientens obetalda besök närmast journalens datum.
  const payable = useMemo(() => {
    if (paidLocally) return null;
    if (journal.appointment_id && journal.appointment_paid === false) {
      const linked = unpaidAppointments.find(a => a.appointment_id === journal.appointment_id);
      return linked || { appointment_id: journal.appointment_id, _needsFetch: true };
    }
    if (unpaidAppointments.length > 0) {
      const jd = new Date(journal.created_at).getTime();
      return [...unpaidAppointments].sort(
        (a, b) => Math.abs(new Date(a.start_time).getTime() - jd) - Math.abs(new Date(b.start_time).getTime() - jd)
      )[0];
    }
    return null;
  }, [journal, unpaidAppointments, paidLocally]);

  const handleOpenPayment = async () => {
    if (!payable) return;
    if (payable._needsFetch) {
      setLoadingPay(true);
      try {
        const apt = await getAppointment(payable.appointment_id);
        setPayAppointment(apt.appointment || apt);
      } catch (err) {
        console.error('Kunde inte hämta bokningen:', err);
        toast.error('Kunde inte öppna kassan för bokningen');
      } finally {
        setLoadingPay(false);
      }
    } else {
      setPayAppointment(payable);
    }
  };

  const handlePaymentSaved = () => {
    setPayAppointment(null);
    setPaidLocally(true);
    toast.success('Betalning registrerad');
    if (onAppointmentPaid) onAppointmentPaid();
  };

  // Överför till PMO (lokal RPA-agent, se pmoClient.js)
  const [showPmoConfirm, setShowPmoConfirm] = useState(false);
  const [pmoSending, setPmoSending] = useState(false);

  // Lokalt state för redigering
  const [editContent, setEditContent] = useState(journal.content || { sections: [] });

  // Synka från servern bara när man INTE redigerar. Under redigering äger
  // editorn innehållet — en omhämtning av posten (t.ex. efter betalning av
  // besöket) får aldrig skriva över osparad text (Spår J). Komponenten har
  // key={entry_id}, så byte av post ger en ny instans.
  useEffect(() => {
    if (!isEditing) setEditContent(journal.content || { sections: [] });
  }, [journal, isEditing]);

  // ── Autospar av utkast (Spår J) ─────────────────────────────────────────
  // Pausas medan dikteringen bearbetar/genererar för den här posten — då
  // skriver kontextet innehållet server-side.
  const rec = useRecordingSession();
  const dictationBusy = Boolean(
    rec && rec.entryId === journal.entry_id &&
    (rec.phase === 'processing' || rec.phase === 'generating')
  );
  const autosaveEnabled = !readOnly && isEditing && journal.status === 'draft';

  const handleSignedElsewhere = useCallback(() => {
    toast.warning('Journalen har signerats någon annanstans — autosparen är stoppad.');
  }, []);

  const autosave = useDraftAutosave({
    entryId: journal.entry_id,
    enabled: autosaveEnabled,
    paused: dictationBusy,
    content: editContent,
    serverContent: journal.content,
    serverVersion: journal.version,
    onSaved: onAutosaved,
    onSignedElsewhere: handleSignedElsewhere,
  });
  const { flush: autosaveFlush, markSaved } = autosave;

  // Låt dikteringen spara lokalt skriven text innan den hämtar baslinjen.
  // getContent: reservbaslinje om sparningen inte går igenom.
  const editContentRef = useRef(editContent);
  editContentRef.current = editContent;
  const registerFlush = rec?.registerFlush;
  useEffect(() => {
    if (!autosaveEnabled || !registerFlush) return undefined;
    return registerFlush(journal.entry_id, autosaveFlush, () => editContentRef.current);
  }, [autosaveEnabled, registerFlush, journal.entry_id, autosaveFlush]);

  const [resolvingConflict, setResolvingConflict] = useState(false);

  // Konflikt: ladda om serverns version (mina osparade ändringar slängs)
  const handleConflictReload = async () => {
    setResolvingConflict(true);
    try {
      const fresh = await getJournalEntry(journal.entry_id);
      if (fresh.status === 'signed') {
        toast.warning('Journalen har signerats någon annanstans.');
      }
      const freshContent = fresh.content || { sections: [] };
      autosave.acceptServer(freshContent, fresh.version);
      setEditContent(freshContent);
      if (onAutosaved) {
        onAutosaved({ content: freshContent, version: fresh.version, updated_at: fresh.updated_at });
      }
    } catch (err) {
      console.error('Kunde inte ladda om journalen:', err);
      toast.error('Kunde inte ladda om journalen');
    } finally {
      setResolvingConflict(false);
    }
  };

  // Konflikt: behåll mitt innehåll och skriv över serverns version
  const handleConflictKeepMine = async () => {
    setResolvingConflict(true);
    try {
      const fresh = await getJournalEntry(journal.entry_id);
      if (fresh.status === 'signed') {
        toast.error('Journalen har redan signerats — dina ändringar kan inte sparas.');
        return;
      }
      const ok = await autosave.keepMine(fresh.version);
      if (ok) toast.success('Dina ändringar är sparade');
    } catch (err) {
      console.error('Kunde inte spara mina ändringar:', err);
      toast.error('Kunde inte spara dina ändringar');
    } finally {
      setResolvingConflict(false);
    }
  };

  // Formatera datum
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Hantera sektionsändring
  const handleSectionChange = (index, newContent) => {
    setEditContent(prev => {
      const newSections = [...(prev.sections || [])];
      newSections[index] = { ...newSections[index], content: newContent };
      return { ...prev, sections: newSections };
    });
  };

  // Fria rubriker (Spår 3): byt namn på rubrik, lägg till / ta bort sektion.
  // Sektionerna är en snapshot i content-JSON (inte FK-låsta till mallen), så
  // hela content PUT:as via handleSave och backend persisterar godtyckligt.
  const handleTitleChange = (index, newTitle) => {
    setEditContent(prev => {
      const newSections = [...(prev.sections || [])];
      newSections[index] = { ...newSections[index], title: newTitle };
      return { ...prev, sections: newSections };
    });
  };

  const handleAddSection = () => {
    setEditContent(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { title: 'Ny rubrik', content: '' }]
    }));
  };

  const handleRemoveSection = (index) => {
    setEditContent(prev => {
      const newSections = [...(prev.sections || [])];
      newSections.splice(index, 1);
      return { ...prev, sections: newSections };
    });
  };

  // "Stäng": utkastet sparas automatiskt — spara det sista och stäng.
  // Går sparningen inte igenom (nätverk/konflikt) förblir editorn öppen.
  const [closing, setClosing] = useState(false);
  const handleClose = async () => {
    setClosing(true);
    try {
      const ok = autosaveEnabled ? await autosave.flush() : true;
      if (ok) {
        onCancel();
      } else {
        toast.error('Utkastet är inte sparat — åtgärda felet innan du stänger.');
      }
    } finally {
      setClosing(false);
    }
  };

  // Hantera signera — skicka ALLTID med aktuellt editContent så att servern
  // sparar och signerar atomärt (annars kunde osparad text tappas, Spår F).
  // Autosparen pausas och pågående sparning väntas in först, så att ingen
  // autosparning landar efter signeringen (403) eller krockar med den.
  const handleSign = async () => {
    if (!autosaveEnabled) {
      onSign(editContent, journal.version);
      return;
    }
    await autosave.suspend();
    try {
      // Versionen följer med: servern vägrar (409) om utkastet ändrats
      // någon annanstans, så att äldre text aldrig signeras och låses.
      const result = await onSign(editContent, autosave.getVersion());
      if (result && result.conflict) autosave.markConflict();
    } finally {
      autosave.resume();
    }
  };

  // Diktering via Aion Notes: kontextet mergar genererad text in i utkastet
  // och sparar server-side — här uppdaterar vi editorn och autosparens
  // baslinje (version) med resultatet.
  const handleGeneratedContent = useCallback((content, meta = {}) => {
    const next = content || { sections: [] };
    setEditContent(next);
    markSaved(next, meta.version);
    if (onAutosaved) onAutosaved({ content: next, version: meta.version });
  }, [markSaved, onAutosaved]);

  // Indikator bredvid knapparna: "Sparar…" / "Sparat HH:MM" / "Osparade ändringar"
  const autosaveLabel = (() => {
    if (!autosaveEnabled) return null;
    switch (autosave.status) {
      case 'saving':
        return { cls: 'saving', icon: 'fa-spinner fa-spin', text: 'Sparar…' };
      case 'dirty':
        return {
          cls: 'unsaved',
          icon: 'fa-circle',
          text: dictationBusy ? 'Väntar på dikteringen…' : 'Osparade ändringar',
        };
      case 'error':
        return { cls: 'unsaved', icon: 'fa-exclamation-triangle', text: 'Kunde inte spara — försöker igen' };
      case 'conflict':
        return { cls: 'unsaved', icon: 'fa-exclamation-triangle', text: 'Konflikt — inte sparat' };
      case 'signed':
        return { cls: 'unsaved', icon: 'fa-lock', text: 'Signerad någon annanstans' };
      case 'saved':
        return autosave.lastSavedAt
          ? {
              cls: 'saved',
              icon: 'fa-check',
              text: `Sparat ${autosave.lastSavedAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}`,
            }
          : null;
      default:
        return null;
    }
  })();

  // ── Överför till PMO ───────────────────────────────────────────────────────
  // Serialiserar den öppna anteckningens sektioner och POSTar till den lokala
  // RPA-agenten (127.0.0.1:5011). Agenten klistrar in i den PMO-post som är öppen
  // i Citrix — därför bekräftar användaren FÖRST att rätt patient är öppen.
  // Ingen patientidentitet skickas (bara title+content).

  // Serialisera det som visas: editContent hålls i synk med journal.content även
  // i läsläge, så det speglar alltid det man ser på skärmen.
  const pmoRubriker = useMemo(
    () => serializeSectionsForPmo(editContent),
    [editContent]
  );

  // Har anteckningen någon text alls att föra över?
  const hasPmoContent = useMemo(
    () => pmoRubriker.some((r) => r.content && r.content.trim()),
    [pmoRubriker]
  );

  const handlePmoConfirm = async () => {
    setPmoSending(true);
    try {
      const result = await transferToPmo(pmoRubriker);
      if (result.outcome === 'ok') {
        toast.success('Journaltexten fördes över till PMO');
      } else if (result.outcome === 'agent_error') {
        toast.error(
          `PMO-agenten svarade med fel (${result.status}). ` +
          'Kontrollera att rätt PMO-post är öppen med första fältet i fokus.'
        );
      } else {
        // agent_down
        toast.error('PMO-agenten körs inte på den här datorn.');
      }
    } catch (err) {
      console.error('Oväntat fel vid PMO-överföring:', err);
      toast.error('Kunde inte föra över till PMO.');
    } finally {
      setPmoSending(false);
      setShowPmoConfirm(false);
    }
  };

  // Hämta sektioner
  const sections = journal.content?.sections || [];
  const editSections = editContent?.sections || [];

  return (
    <div className="journal-content">
      {/* Header */}
      <div className="journal-content-header">
        <div className="journal-header-info">
          <h3 className="journal-title">
            {journal.display_name || journal.template_name || 'Journal'}
          </h3>
          <div className="journal-meta">
            <span className="journal-datetime">
              <i className="fas fa-clock"></i> {formatDateTime(journal.created_at)}
            </span>
            <span className="journal-author">
              <i className="fas fa-user-md"></i> {journal.user_name}
            </span>
          </div>
        </div>
        <div className="journal-header-status">
          {journal.status === 'signed' ? (
            <span className="status-badge status-signed">
              <i className="fas fa-check-circle"></i> Signerad
              <span className="signed-info">
                {formatDateTime(journal.signed_at)} av {journal.signed_by_name}
              </span>
            </span>
          ) : (
            <span className="status-badge status-draft">
              <i className="fas fa-edit"></i> Utkast
            </span>
          )}
        </div>
      </div>

      {/* Diktering via Aion Notes (endast kombokliniker, endast utkast) */}
      {!readOnly && isEditing && journal.status === 'draft' && (
        <RecordingPanel
          journal={journal}
          patientName={journal.patient?.fullname}
          onContentGenerated={handleGeneratedContent}
          autoStart={autoRecord}
          onAutoStartConsumed={onAutoRecordConsumed}
        />
      )}

      {/* Sektioner.
          Läsläge: visa bara rubriker som har innehåll.
          Redigeringsläge: redigerbara rubriker (fria rubriker, Spår 3), alla
          sektioner visas, och man kan lägga till / ta bort sektioner. */}
      {autosaveEnabled && autosave.status === 'conflict' && (
        <div className="jc-autosave-conflict" role="alert">
          <div className="jc-autosave-conflict-text">
            <i className="fas fa-exclamation-triangle"></i>{' '}
            <strong>Utkastet har ändrats någon annanstans</strong> (annan flik eller dator).
            Dina senaste ändringar är inte sparade.
          </div>
          <div className="jc-autosave-conflict-actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleConflictReload}
              disabled={resolvingConflict}
            >
              <i className="fas fa-sync"></i> Ladda om
            </button>
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={handleConflictKeepMine}
              disabled={resolvingConflict}
            >
              <i className="fas fa-save"></i> Behåll min
            </button>
          </div>
        </div>
      )}

      <div
        className="journal-sections"
        onBlur={autosaveEnabled ? autosave.saveNow : undefined}
      >
        {isEditing ? (
          <>
            {editSections.map((section, index) => (
              <div key={index} className="journal-section journal-section-editing">
                <div className="section-title-row">
                  <input
                    type="text"
                    className="section-title-input"
                    value={section.title || ''}
                    onChange={(e) => handleTitleChange(index, e.target.value)}
                    placeholder="Rubriknamn"
                    readOnly={dictationBusy}
                  />
                  <button
                    type="button"
                    className="section-remove-btn"
                    onClick={() => handleRemoveSection(index)}
                    title="Ta bort rubrik"
                    disabled={dictationBusy}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
                {useInternalJournal ? (
                  <TextAreaWithPhrases
                    className="section-editor"
                    value={section.content || ''}
                    onChange={(e) => handleSectionChange(index, e.target.value)}
                    placeholder={`Skriv ${(section.title || 'text').toLowerCase()}...`}
                    rows={4}
                    readOnly={dictationBusy}
                  />
                ) : (
                  <textarea
                    className="section-editor"
                    value={section.content || ''}
                    onChange={(e) => handleSectionChange(index, e.target.value)}
                    placeholder={`Skriv ${(section.title || 'text').toLowerCase()}...`}
                    rows={4}
                    readOnly={dictationBusy}
                  />
                )}
              </div>
            ))}
            <button
              type="button"
              className="section-add-btn"
              onClick={handleAddSection}
              disabled={dictationBusy}
            >
              <i className="fas fa-plus"></i> Lägg till rubrik
            </button>
          </>
        ) : (
          (() => {
            const visibleSections = sections.filter(
              (section) => section.content && section.content.trim()
            );

            if (visibleSections.length === 0) {
              return (
                <div className="journal-no-content">
                  <p>Ingen journaltext ännu</p>
                </div>
              );
            }

            return visibleSections.map((section, index) => (
              <div key={index} className="journal-section">
                <h4 className="section-title">{section.title}</h4>
                <div className="section-content">{section.content}</div>
              </div>
            ));
          })()
        )}
      </div>

      {/* Signerat tillägg (Spår 4): på signerad anteckning kan ursprunglig
          författare lägga append-only tillägg. Ingen fri redigering. */}
      {journal.status === 'signed' && (
        <JournalAmendments entryId={journal.entry_id} readOnly={readOnly} />
      )}

      {/* Åtgärdsknappar (inga i skrivskyddad referensvy) */}
      {!readOnly && (
      <div className="journal-actions">
        {autosaveLabel && (
          <span
            className={`autosave-indicator autosave-indicator--${autosaveLabel.cls} jc-autosave-indicator`}
            title="Utkastet sparas automatiskt"
          >
            <i className={`fas ${autosaveLabel.icon}`}></i> {autosaveLabel.text}
          </span>
        )}
        {/* Betala-knapp för obetalt besök — syns i alla lägen */}
        {payable && (
          <button
            className="btn btn-warning btn-sm journal-pay-btn"
            onClick={handleOpenPayment}
            disabled={loadingPay}
            title="Registrera betalning för besöket"
          >
            <i className="fas fa-cash-register"></i> {loadingPay ? 'Öppnar…' : 'Betala besök'}
          </button>
        )}
        {/* Överför journaltext till PMO via lokal RPA-agent — syns i alla lägen
            så länge anteckningen har innehåll. */}
        {hasPmoContent && (
          <button
            className="btn btn-outline-primary btn-sm journal-pmo-btn"
            onClick={() => setShowPmoConfirm(true)}
            disabled={pmoSending}
            title="För över journaltexten till öppen PMO-post"
          >
            <i className="fas fa-file-export"></i> {pmoSending ? 'Överför…' : 'Överför till PMO'}
          </button>
        )}
        {isEditing ? (
          <>
            <button
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={saving || closing || dictationBusy}
              title="Utkastet sparas automatiskt"
            >
              {closing ? 'Sparar…' : 'Stäng'}
            </button>
            {journal.status === 'draft' && (
              <button
                className="btn btn-success"
                onClick={handleSign}
                disabled={
                  saving || closing || dictationBusy ||
                  autosave.status === 'conflict' || autosave.status === 'signed'
                }
              >
                Signera
              </button>
            )}
          </>
        ) : journal.status === 'draft' ? (
          <>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={onDelete}
            >
              <i className="fas fa-trash"></i> Ta bort
            </button>
            <button
              className="btn btn-secondary"
              onClick={onEdit}
              disabled={dictationBusy}
            >
              <i className="fas fa-edit"></i> Redigera
            </button>
            <button
              className="btn btn-success"
              onClick={handleSign}
            >
              <i className="fas fa-check"></i> Signera
            </button>
          </>
        ) : (
          <div className="journal-signed-notice">
            <i className="fas fa-lock"></i> Denna journal är signerad och kan inte redigeras
          </div>
        )}
      </div>
      )}

      {payAppointment && (
        <PaymentModal
          appointment={payAppointment}
          onClose={() => setPayAppointment(null)}
          onSave={handlePaymentSaved}
        />
      )}

      {/* Patientsäkerhet: agenten klistrar i den PMO-post som RÅKAR vara öppen —
          ingen patientmatchning sker. Kräv därför uttrycklig bekräftelse. */}
      <ConfirmDialog
        isOpen={showPmoConfirm}
        variant="warning"
        title="Är rätt patient öppen i PMO?"
        message={
          `Texten klistras in i den journal som just nu är öppen i PMO (Citrix). ` +
          `Kontrollera att rätt patient är öppen och att första fältet är i fokus. ` +
          `${pmoRubriker.length} rubrik${pmoRubriker.length === 1 ? '' : 'er'} förs över. ` +
          `Ingen patientidentitet skickas.`
        }
        confirmLabel="Överför till PMO"
        cancelLabel="Avbryt"
        loading={pmoSending}
        onConfirm={handlePmoConfirm}
        onCancel={() => setShowPmoConfirm(false)}
      />
    </div>
  );
};

export default JournalContent;
